<script>
import documentControlAPI from "@/services/documentControl/documentControlAPI"
import Swal from "sweetalert2"

export default {
  name: "DocumentControlSection",
  props: {
    companyId: { type: [Number, String], required: true },
  },
  data() {
    return {
      rows: [],
      loading: true,
      savingType: null,
      search: "",
    }
  },
  computed: {
    filteredRows() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.rows
      return this.rows.filter(
        (r) =>
          r.label.toLowerCase().includes(q) ||
          (r.codigo || "").toLowerCase().includes(q) ||
          (r.item || "").toLowerCase().includes(q)
      )
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await documentControlAPI.list(this.companyId)
        this.rows = (res && res.data) || []
      } catch (e) {
        console.log(e)
        this.rows = []
      }
      this.loading = false
    },

    async save(row) {
      if (!row.codigo || !row.codigo.trim()) {
        Swal.fire({
          title: "Código requerido",
          text: "El código no puede quedar vacío.",
          icon: "warning",
          confirmButtonClass: "btn btn-confirm mt-2",
        })
        return
      }

      this.savingType = row.document_type
      try {
        const payload = { codigo: row.codigo.trim() }
        // La versión solo viaja mientras la empresa no haya fijado su punto de
        // partida; después el backend la ignora y solo avanza por consecutivo.
        if (row.version_editable && row.version) payload.version = String(row.version).trim()

        const res = await documentControlAPI.upsert(this.companyId, row.document_type, payload)
        if (res && res.data) this.applyRow(row, res.data)

        Swal.fire({
          title: "¡Guardado!",
          text: "El control documental se actualizó.",
          icon: "success",
          confirmButtonClass: "btn btn-confirm mt-2",
        })
      } catch (e) {
        console.log(e)
        Swal.fire({
          title: "¡ERROR!",
          text: "No se pudo guardar el control documental.",
          icon: "error",
          confirmButtonClass: "btn btn-confirm mt-2",
        })
      }
      this.savingType = null
    },

    async newVersion(row) {
      const result = await Swal.fire({
        title: "¿Emitir nueva versión?",
        html: `Se avanzará el consecutivo de <b>${row.label}</b> y se registrará hoy como fecha de aprobación.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, emitir",
        cancelButtonText: "Cancelar",
        confirmButtonClass: "btn btn-confirm mt-2",
      })
      if (!result.value && !result.isConfirmed) return

      this.savingType = row.document_type
      try {
        const res = await documentControlAPI.newVersion(this.companyId, row.document_type)
        if (res && res.data) this.applyRow(row, res.data)
        Swal.fire({
          title: "¡Listo!",
          text: (res && res.message) || "Versión actualizada.",
          icon: "success",
          confirmButtonClass: "btn btn-confirm mt-2",
        })
      } catch (e) {
        console.log(e)
        Swal.fire({
          title: "¡ERROR!",
          text: "No se pudo emitir la nueva versión.",
          icon: "error",
          confirmButtonClass: "btn btn-confirm mt-2",
        })
      }
      this.savingType = null
    },

    /** Refleja en la fila lo que devolvió el backend (fuente de verdad). */
    applyRow(row, data) {
      this.$set(row, "codigo", data.codigo)
      this.$set(row, "version", data.version)
      this.$set(row, "fecha_aprobacion", (data.fecha_aprobacion || "").substring(0, 10))
      this.$set(row, "personalizado", true)
      this.$set(row, "version_editable", false)
    },
  },
}
</script>

<template>
  <div class="card">
    <div class="card-body">
      <h5 class="card-title">Control documental</h5>
      <p class="text-muted small">
        Código y versión que aparecen en el encabezado de los PDF que genera la plataforma.
        Cada empresa maneja su propia nomenclatura: ajusta el código y escribe la versión
        en la que va actualmente. Una vez guardada, la versión ya no se escribe a mano —
        avanza con el botón <b>Nueva versión</b>. La fecha del encabezado siempre es la del
        día en que se genera el documento.
      </p>

      <div v-if="loading" class="text-center my-4">
        <b-spinner small variant="primary"></b-spinner>
      </div>

      <div v-else>
        <div class="row mb-2">
          <div class="col-md-5">
            <b-form-input v-model="search" placeholder="Buscar por ítem, formato o código" size="sm"></b-form-input>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table table-sm table-striped align-middle">
            <thead>
              <tr>
                <th style="width: 70px;">Ítem</th>
                <th>Formato</th>
                <th style="width: 160px;">Código</th>
                <th style="width: 110px;">Versión</th>
                <th style="width: 130px;">Aprobación</th>
                <th style="width: 190px;" class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredRows" :key="row.document_type">
                <td>
                  <span class="badge badge-soft-primary">{{ row.item || "—" }}</span>
                </td>
                <td>
                  {{ row.label }}
                  <div v-if="!row.personalizado" class="text-muted small">
                    Usando el código por defecto ({{ row.codigo_default }})
                  </div>
                </td>
                <td>
                  <b-form-input v-model="row.codigo" size="sm" maxlength="30"></b-form-input>
                </td>
                <td>
                  <b-form-input
                    v-if="row.version_editable"
                    v-model="row.version"
                    size="sm"
                    maxlength="10"
                    placeholder="Ej: 3.8"
                  ></b-form-input>
                  <span v-else class="badge badge-soft-success">{{ row.version }}</span>
                </td>
                <td class="text-muted small">{{ row.fecha_aprobacion || "—" }}</td>
                <td class="text-center">
                  <b-spinner v-if="savingType === row.document_type" small variant="primary"></b-spinner>
                  <template v-else>
                    <button type="button" class="btn btn-primary btn-sm mr-1" @click="save(row)">
                      Guardar
                    </button>
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      :disabled="row.version_editable"
                      :title="row.version_editable
                        ? 'Primero guarda la versión en la que va la empresa'
                        : 'Avanza el consecutivo'"
                      @click="newVersion(row)"
                    >
                      Nueva versión
                    </button>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
